import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { useDismiss } from './useDismiss';

export interface MenuItem {
  label: string;
  /** Пояснение под пунктом */
  description?: string;
  /** Опасное действие — красным и после линии */
  danger?: boolean;
  onClick: () => void;
}

/**
 * Меню «⋯» `.menu .mi` (кит v2.0, 2.11): плоский список, недоступные
 * пункты не показываются, опасные — после линии. Меню из одного пункта
 * не бывает (кит 2.12) — для этого RowActions рисует кнопку.
 */
export function MenuList({ items, onPick }: { items: MenuItem[]; onPick: () => void }) {
  const safe = items.filter((i) => !i.danger);
  const danger = items.filter((i) => i.danger);
  const render = (i: MenuItem) => (
    <button
      key={i.label}
      type="button"
      className={i.danger ? 'mi dz' : 'mi'}
      onClick={() => {
        onPick();
        i.onClick();
      }}
    >
      {i.label}
      {i.description ? <span className="d">{i.description}</span> : null}
    </button>
  );
  return (
    <div className="menu" onClick={(e) => e.stopPropagation()}>
      {safe.map(render)}
      {safe.length && danger.length ? <hr className="hair" /> : null}
      {danger.map(render)}
    </div>
  );
}

/** «⋯» в шапке объекта — рядом с главной кнопкой (`.acts`). */
export function ActionMenu({ items }: { items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, wrap);
  if (!items.length) return null;
  return (
    <span ref={wrap} className="rmw">
      <Button icon title="Ещё действия" onClick={() => setOpen((v) => !v)}>
        <svg>
          <use href="#i-dots" />
        </svg>
      </Button>
      {open ? <MenuList items={items} onPick={close} /> : null}
    </span>
  );
}

/**
 * Действия в строке связи (ТЗ M2 §8): несколько — «⋯» `.rmw`, одно —
 * кнопкой с текстом, потому что «⋯» с одним пунктом кит запрещает.
 */
export function RowActions({ items }: { items: MenuItem[] }): ReactNode {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, wrap);
  if (!items.length) return null;
  if (items.length === 1) {
    const [only] = items;
    return (
      <Button
        tone="ghost"
        small
        title={only.description}
        onClick={(e) => {
          e.stopPropagation();
          only.onClick();
        }}
      >
        {only.label}
      </Button>
    );
  }
  return (
    <span className="rmw" ref={wrap} onClick={(e) => e.stopPropagation()}>
      <Button tone="ghost" small icon title="Действия" onClick={() => setOpen((v) => !v)}>
        <svg>
          <use href="#i-dots" />
        </svg>
      </Button>
      {open ? <MenuList items={items} onPick={close} /> : null}
    </span>
  );
}
